import { formatPropertyNames } from "./propertyDisplay";

/**
 * userDisplay.ts — helpers para construir labels de usuarios en selects.
 *
 * Round 2026-05-09: el admin pidió que TODOS los selects de cliente/
 * propietario muestren la propiedad asociada para no tener que saber
 * de memoria a qué arriendo corresponde cada persona. Aplica de forma
 * consistente en gastos, sueldos, lavandería, asignación de propietarios
 * a propiedades, y cualquier futuro selector que liste clientes.
 *
 * Reglas:
 *   - Si el usuario tiene 1 propiedad: "Juan Pérez — LATORRE 142"
 *   - Si tiene varias: "Juan Pérez — LATORRE 142, COSTA PUYAI 282"
 *   - Si tiene muchas (>3): "Juan Pérez — LATORRE 142, COSTA PUYAI 282, +2"
 *   - Si NO tiene propiedades (aseador, trabajador, admin): "Juan Pérez"
 *     sin sufijo — no hay nada que mostrar y el em-dash colgaría feo.
 *
 * El backend (`/api/users/finanzas` → `getUsersPerRol`) devuelve el
 * array `propiedades: string[]` con los nombres de propiedad. La
 * asociación cubre las dos vías (FK `id_cliente` + JSONB `propietario_ids`).
 */

/** Subset de campos que necesita el helper. */
export interface UserWithProperties {
  id?: number;
  nombre?: string | null;
  // Algunos endpoints usan nombre_usuario / nombre_propietario en vez de
  // `nombre`. Si vienen, se prefieren al genérico (más completo).
  nombre_usuario?: string | null;
  nombre_propietario?: string | null;
  rol?: string | null;
  propiedades?: string[] | null;
}

const MAX_PROPS_INLINE = 3;

/**
 * Resuelve el nombre canónico del usuario, eligiendo la mejor fuente.
 * Usa `nombre` si viene (output de getUsersPerRol), si no cae a
 * nombre_propietario (cuando el rol es propietario) o nombre_usuario.
 */
export function resolveUserName(user: UserWithProperties): string {
  if (user.nombre) return user.nombre;
  if (user.rol === "propietario") {
    return user.nombre_propietario || user.nombre_usuario || "—";
  }
  return user.nombre_usuario || user.nombre_propietario || "—";
}

/**
 * Construye el label visible del select.
 *   formatUserLabel({ nombre: "Juan", propiedades: ["A", "B"] })
 *     → "Juan — A, B"
 *   formatUserLabel({ nombre: "Juan", propiedades: [] })
 *     → "Juan"
 */
export function formatUserLabel(user: UserWithProperties): string {
  const nombre = resolveUserName(user);
  // Los nombres de propiedad SIEMPRE se muestran en MAYÚSCULAS (round
  // 2026-06-28, pedido del owner). Solo presentación — el nombre del
  // usuario conserva su capitalización. Ver utils/propertyDisplay.ts.
  const props = formatPropertyNames(user.propiedades);
  if (props.length === 0) return nombre;
  if (props.length <= MAX_PROPS_INLINE) {
    return `${nombre} — ${props.join(", ")}`;
  }
  const head = props.slice(0, MAX_PROPS_INLINE).join(", ");
  const rest = props.length - MAX_PROPS_INLINE;
  return `${nombre} — ${head}, +${rest}`;
}

/**
 * Conveniencia: construye `{ value, label }` listo para un AntD Select.
 * El value sigue siendo el `id` (número), no el label — los formularios
 * persisten el ID y eso no debe cambiar.
 */
export function userSelectOption(user: UserWithProperties): {
  value: number;
  label: string;
} {
  return {
    value: Number(user.id),
    label: formatUserLabel(user),
  };
}
